<template>
  <header
    class="sticky top-0 z-40 w-full bg-white/90 dark:bg-[#0B1120]/90 backdrop-blur-xl border-b border-slate-200/80 dark:border-slate-800/80 transition-all duration-200 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
  >
    <div class="px-3 sm:px-6 py-2.5 sm:py-3.5">
      <div class="flex items-center justify-between gap-3 min-w-0">
        <!-- LEFT: Mobile Menu Toggle & Brand / Desktop Breadcrumb Stack -->
        <div class="flex items-center gap-2.5 sm:gap-4 min-w-0">
          <!-- Mobile Hamburger Toggle -->
          <button
            type="button"
            @click="toggleMobileSidebar"
            class="lg:hidden p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-800/70 border border-slate-200/70 dark:border-slate-800 transition-colors shrink-0"
            aria-label="Toggle navigation menu"
          >
            <Menu class="w-5 h-5" />
          </button>

          <!-- Mobile Brand Pill (Shown when screen < md) -->
          <div class="flex items-center gap-2 md:hidden min-w-0">
            <div
              class="w-7 h-7 rounded-lg bg-gradient-to-tr from-indigo-600 to-violet-500 text-white flex items-center justify-center font-bold text-xs shadow-sm shadow-indigo-500/30 shrink-0"
            >
              <GraduationCap class="w-4 h-4" />
            </div>
            <div class="min-w-0">
              <h1 class="text-sm font-bold text-slate-900 dark:text-white truncate leading-tight">
                {{ currentPageMeta.title }}
              </h1>
            </div>
          </div>

          <!-- Desktop Page Meta & Contextual Breadcrumb (md+) -->
          <div class="hidden md:flex flex-col min-w-0">
            <div class="flex items-center gap-2 text-xs font-semibold text-slate-400 dark:text-slate-500">
              <span class="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">EduManage</span>
              <span>/</span>
              <span class="text-indigo-600 dark:text-indigo-400">{{ currentPageMeta.category }}</span>
            </div>

            <div class="flex items-center gap-2.5 mt-0.5">
              <h2 class="text-base lg:text-lg font-bold text-slate-900 dark:text-white tracking-tight truncate">
                {{ currentPageMeta.title }}
              </h2>
              <span
                class="hidden xl:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 border border-slate-200/70 dark:border-slate-700/60"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>{{ currentPageMeta.subtitle }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- RIGHT: Action Items Cluster (Calendar, Stats, Theme, Refresh, Alerts, User) -->
        <div class="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
          <!-- XL Mini Stats Overview -->
          <TodaysData class="hidden 2xl:flex" />

          <!-- Date Chip (Desktop md+) -->
          <div
            class="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300"
          >
            <CalendarIcon class="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
            <span>{{ liveDateString }}</span>
          </div>

          <!-- Google Calendar Trigger Modal -->
          <Calender />

          <!-- Refresh Button -->
          <RefreshButton />

          <!-- Theme Toggle (Dark / Light) -->
          <ThemeToggle />

          <!-- Notification Center Button -->
          <div class="relative">
            <button
              type="button"
              @click="toggleNotifications"
              class="relative p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-800/70 rounded-xl transition-all duration-150 border border-transparent hover:border-slate-200/80 dark:hover:border-slate-800"
              title="Notifications"
              aria-label="View notifications"
            >
              <Bell class="w-4 h-4 sm:w-5 sm:h-5" />
              <span
                v-if="unreadCount > 0"
                class="absolute 1 top-1.5 right-1.5 flex h-2.5 w-2.5"
              >
                <span
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-600 border-2 border-white dark:border-[#0B1120]"
                ></span>
              </span>
            </button>

            <!-- Notifications Flyout Menu -->
            <transition name="popover">
              <div
                v-if="showNotifications"
                class="fixed sm:absolute inset-x-3 top-16 sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-96 max-h-[80vh] sm:max-h-[460px] bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl border border-slate-200/90 dark:border-[#263449] z-[10000] overflow-hidden flex flex-col"
              >
                <!-- Flyout Header -->
                <div
                  class="p-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between bg-slate-50/70 dark:bg-[#18243A]/60"
                >
                  <div class="flex items-center gap-2">
                    <h3 class="text-sm font-bold text-slate-900 dark:text-white">
                      Notifications
                    </h3>
                    <span
                      v-if="unreadCount > 0"
                      class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60"
                    >
                      {{ unreadCount }} new
                    </span>
                  </div>
                  <button
                    type="button"
                    @click="showNotifications = false"
                    class="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-700 transition"
                  >
                    <X class="w-4 h-4" />
                  </button>
                </div>

                <!-- Flyout Body / List -->
                <div class="flex-1 overflow-y-auto p-2 space-y-1.5 custom-scrollbar">
                  <div
                    v-if="loading"
                    class="py-10 text-center text-xs text-slate-500 dark:text-slate-400"
                  >
                    <div class="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    Updating notifications...
                  </div>

                  <div
                    v-else-if="notifications.length === 0"
                    class="py-12 px-4 text-center"
                  >
                    <div class="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center mx-auto mb-2">
                      <Bell class="w-5 h-5 opacity-60" />
                    </div>
                    <p class="text-xs font-semibold text-slate-800 dark:text-slate-200">
                      All caught up!
                    </p>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      No unread school notifications.
                    </p>
                  </div>

                  <div
                    v-else
                    v-for="n in notifications"
                    :key="n.leaveId || n._id || Math.random()"
                    @click="markAsRead(n.leaveId)"
                    class="p-3 rounded-xl border transition-all duration-150 cursor-pointer text-left"
                    :class="getNotificationClass(n.type, n.read)"
                  >
                    <div class="flex items-start justify-between gap-2">
                      <p class="text-xs font-medium leading-snug">
                        {{ n.message }}
                      </p>
                      <span
                        v-if="!n.read"
                        class="w-2 h-2 rounded-full bg-indigo-600 shrink-0 mt-1"
                      ></span>
                    </div>
                    <p class="text-[10px] opacity-60 mt-1.5 flex items-center gap-1">
                      <Clock class="w-3 h-3" />
                      <span>{{ formatTime(n.date) }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- User Profile Dropdown Pill -->
          <UserProfile />
        </div>
      </div>
    </div>

    <!-- Backdrop Overlay for Mobile Flyout -->
    <div
      v-if="showNotifications"
      @click="showNotifications = false"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-[9999] sm:hidden"
    ></div>
  </header>
</template>

<script>
  import {
    Bell,
    X,
    Menu,
    GraduationCap,
    Clock,
    Calendar as CalendarIcon,
  } from "lucide-vue-next";
  import Calender from "./Calender/Calender.vue";
  import UserProfile from "./UserProfile/UserProfile .vue";
  import TodaysData from "./TodaysData/TodaysData.vue";
  import RefreshButton from "./Refresh/Refresh.vue";
  import ThemeToggle from "./ThemeToggle/ThemeToggle.vue";
  import { useSidebar } from "@/composables/useSidebar";

  export default {
    name: "SchoolHeader",
    components: {
      TodaysData,
      Bell,
      UserProfile,
      Calender,
      RefreshButton,
      ThemeToggle,
      X,
      Menu,
      GraduationCap,
      Clock,
      CalendarIcon,
    },
    setup() {
      const { toggleMobileSidebar } = useSidebar();
      return { toggleMobileSidebar };
    },
    data() {
      return {
        showNotifications: false,
        loading: false,
      };
    },
    computed: {
      notifications() {
        return this.$store.state.notifications || [];
      },
      unreadCount() {
        return this.notifications.filter((n) => !n.read).length;
      },
      liveDateString() {
        const now = new Date();
        return now.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
      },
      currentPageMeta() {
        const path = this.$route.path;

        if (path === "/" || path === "") {
          return {
            category: "Core",
            title: "Command Center",
            subtitle: "Academic Year 2026-27",
          };
        }
        if (path.startsWith("/students")) {
          return {
            category: "Academics",
            title: "Student Directory",
            subtitle: "Active Roster",
          };
        }
        if (path.startsWith("/teacherAttendance")) {
          return {
            category: "Academics",
            title: "Faculty Attendance",
            subtitle: "Daily Check-in",
          };
        }
        if (path.startsWith("/teacher")) {
          return {
            category: "Academics",
            title: "Teaching Faculty",
            subtitle: "Instructor Profiles",
          };
        }
        if (path.startsWith("/classes")) {
          return {
            category: "Academics",
            title: "Class Management",
            subtitle: "Curriculum & Sections",
          };
        }
        if (path.startsWith("/leaveRequest")) {
          return {
            category: "Academics",
            title: "Leave Requests",
            subtitle: "Applications & Status",
          };
        }
        if (path.startsWith("/attendance")) {
          return {
            category: "Academics",
            title: "Student Attendance",
            subtitle: "Daily Tracking",
          };
        }
        if (path.startsWith("/fee-management") || path.startsWith("/fees")) {
          return {
            category: "Administration",
            title: "Fee Management",
            subtitle: "Collections & Invoices",
          };
        }
        if (path.startsWith("/devices")) {
          return {
            category: "Administration",
            title: "Smart Cards & Devices",
            subtitle: "Hardware Integrations",
          };
        }
        if (path.startsWith("/device-operations")) {
          return {
            category: "Administration",
            title: "Device Operations",
            subtitle: "Terminal Diagnostics",
          };
        }
        if (path.startsWith("/WEBDashboard")) {
          return {
            category: "Administration",
            title: "Website Studio",
            subtitle: "Public Portal CMS",
          };
        }
        if (path.startsWith("/report")) {
          return {
            category: "Administration",
            title: "Faculty Announcements",
            subtitle: "Broadcasts & Bulletins",
          };
        }
        if (path.startsWith("/features")) {
          return {
            category: "Administration",
            title: "Platform Features",
            subtitle: "Smart Modules",
          };
        }
        if (path.toLowerCase().startsWith("/help")) {
          return {
            category: "Support",
            title: "Help & Support",
            subtitle: "Documentation & Desk",
          };
        }
        if (path.startsWith("/profile")) {
          return {
            category: "Account",
            title: "User Profile",
            subtitle: "Settings & Credentials",
          };
        }

        return {
          category: "Portal",
          title: "EduManage",
          subtitle: "School Administration",
        };
      },
    },
    methods: {
      getNotificationClass(type, isRead) {
        if (isRead) {
          return "bg-slate-50/50 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200/60 dark:border-slate-800";
        }
        switch (type) {
          case "leave_approved":
            return "bg-emerald-50/70 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/50 hover:bg-emerald-50 dark:hover:bg-emerald-950/60";
          case "leave_declined":
            return "bg-rose-50/70 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800/50 hover:bg-rose-50 dark:hover:bg-rose-950/60";
          case "leave_applied":
          case "leave_application":
            return "bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/50 hover:bg-indigo-50 dark:hover:bg-indigo-950/60";
          default:
            return "bg-slate-50 dark:bg-slate-800/70 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800";
        }
      },
      async fetchNotifications() {
        this.loading = true;
        try {
          await this.$store.dispatch("fetchNotifications");
        } catch (e) {
          // Keep resilient if backend is uncontactable
        } finally {
          this.loading = false;
        }
      },
      async toggleNotifications() {
        this.showNotifications = !this.showNotifications;
        if (this.showNotifications) {
          await this.fetchNotifications();
        }
      },
      markAsRead(notificationId) {
        const notification = this.notifications.find(
          (n) => n.leaveId === notificationId,
        );
        if (notification && !notification.read) {
          notification.read = true;
        }
      },
      formatTime(timestamp) {
        if (!timestamp) return "Recently";
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);
        if (diffInHours < 1) return "Just now";
        if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
        return date.toLocaleDateString();
      },
    },
    mounted() {
      this.fetchNotifications();
    },
  };
</script>

<style scoped>
  .popover-enter-active,
  .popover-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  .popover-enter-from,
  .popover-leave-to {
    opacity: 0;
    transform: translateY(-8px) scale(0.97);
  }
</style>
